// Phase 6B · Communications outbound page.
// Three sections stacked:
//   1. Compose panel trigger (reusable CustomerEmailPanel)
//   2. Templates CRUD
//   3. Email log (status chips reflect webhook events)
//
// Additive. No locked component modified.
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import CustomerEmailPanel from "@/components/dashboard/CustomerEmailPanel";
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  listEmailLog,
} from "@/lib/commOutbound";

function StatusBadge({ status }) {
  const map = {
    sent: "bg-blue-100 text-blue-800",
    delivered: "bg-green-100 text-green-800",
    bounced: "bg-red-100 text-red-800",
    complained: "bg-amber-100 text-amber-800",
    opened: "bg-indigo-100 text-indigo-800",
    clicked: "bg-purple-100 text-purple-800",
    delayed: "bg-yellow-100 text-yellow-800",
    failed: "bg-red-200 text-red-900",
  };
  const cls = map[status] || "bg-neutral-100 text-neutral-700";
  return (
    <Badge data-testid={`comm-log-status-${status}`} className={cls}>
      {status || "unknown"}
    </Badge>
  );
}

export default function CommunicationOutboundPage() {
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", body_html: "" });

  const refreshTemplates = useCallback(async () => {
    try { setTemplates(await listTemplates()); } catch (e) {}
  }, []);
  const refreshLog = useCallback(async () => {
    try { setLog(await listEmailLog()); } catch (e) {}
  }, []);

  useEffect(() => { refreshTemplates(); refreshLog(); }, [refreshTemplates, refreshLog]);

  const openCreate = () => {
    setEditing({ mode: "create" });
    setForm({ name: "", subject: "", body_html: "" });
  };
  const openEdit = (t) => {
    setEditing({ mode: "edit", id: t.id });
    setForm({ name: t.name, subject: t.subject, body_html: t.body_html });
  };
  const closeEditor = () => setEditing(null);

  const saveTemplate = async () => {
    try {
      if (editing.mode === "create") await createTemplate(form);
      else await updateTemplate(editing.id, form);
      toast.success("Template saved");
      closeEditor();
      refreshTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Save failed");
    }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteTemplate(t.id);
      toast.success("Deleted");
      refreshTemplates();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6 p-6" data-testid="comm-outbound-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Customer email</h1>
          <p className="text-sm text-neutral-500">
            Draft, preview and send outbound customer emails with one-shot human approval.
          </p>
        </div>
        <CustomerEmailPanel
          sourceEntityType="manual"
          triggerLabel="Compose email"
          onSent={() => refreshLog()}
        />
      </div>

      {/* Templates */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Templates</CardTitle>
            <CardDescription>
              Workspace-scoped. Reusable in Compose. Versioned on update.
            </CardDescription>
          </div>
          <Button data-testid="comm-template-new-btn" onClick={openCreate}>
            New template
          </Button>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <p className="text-sm text-neutral-500">No templates yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id} data-testid={`comm-template-row-${t.id}`}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="truncate max-w-[280px]">{t.subject}</TableCell>
                    <TableCell>v{t.version}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        data-testid={`comm-template-edit-${t.id}`}
                        variant="outline" size="sm" onClick={() => openEdit(t)}
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`comm-template-delete-${t.id}`}
                        variant="destructive" size="sm" onClick={() => removeTemplate(t)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Log */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Email log</CardTitle>
            <CardDescription>
              Workspace-scoped audit of outbound sends and webhook events.
            </CardDescription>
          </div>
          <Button
            data-testid="comm-log-refresh-btn"
            variant="outline" onClick={refreshLog}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-sm text-neutral-500">No emails sent yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Provider ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((row) => (
                  <TableRow key={row.id} data-testid={`comm-log-row-${row.id}`}>
                    <TableCell><StatusBadge status={row.status} /></TableCell>
                    <TableCell className="truncate max-w-[220px]">
                      {(row.recipients || []).join(", ")}
                    </TableCell>
                    <TableCell className="truncate max-w-[220px]">
                      {row.safe_subject}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-neutral-500">
                        {row.source_entity_type}
                        {row.source_entity_id ? ` · ${row.source_entity_id}` : ""}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.provider_message_id?.slice(0, 16) || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Template editor dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent data-testid="comm-template-editor" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === "edit" ? "Edit template" : "New template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                data-testid="comm-template-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                data-testid="comm-template-subject-input"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </div>
            <div>
              <Label>Body (HTML)</Label>
              <Textarea
                data-testid="comm-template-body-input"
                rows={10}
                value={form.body_html}
                onChange={(e) => setForm((f) => ({ ...f, body_html: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditor}>Cancel</Button>
            <Button
              data-testid="comm-template-save-btn"
              onClick={saveTemplate}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
